import { useState } from "react";
import type { Audience } from "../../../shared/goals/audience";
import { PnRow } from "../../../shared/phone/Row";
import { ActionSheet } from "../../../shared/phone/Sheet";
import { found } from "../../components/lists/ProductLists";
import { Icon } from "../../components/ui/Icon";
import { listFor, type ListsView } from "../../lib/listsApi";
import { customTags, likeLine, pixelTags, toggleCustom, togglePixel, type CustomAudience, type PixelEvent, type Side, type Tag } from "./ListsBlock";

const n = (x: number) => x.toLocaleString("en-US");

/** A switch as a row of a group: its words on the left, the switch on the right. */
function SwitchRow({ title, sub, on, disabled, onToggle }: { title: string; sub?: string; on: boolean; disabled?: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="pn-row wrap" role="switch" aria-checked={on} disabled={disabled} onClick={onToggle}>
      <span className="m">
        <span className="t">{title}</span>
        {sub && <span className="s">{sub}</span>}
      </span>
      <span className="pn-sw" aria-checked={on} aria-hidden="true" />
    </button>
  );
}

/** Tags as rows: each says whether Meta starts with them or leaves them out, and a tap chooses. */
function TagGroup({ title, note, empty, unread, tags, chosen, editable, onMove }: { title: string; note?: string; empty: string | null; unread: boolean; tags: Tag[]; chosen: Record<Side, string[]>; editable: boolean; onMove: (key: string, from: Side | null, to: Side | null) => void }) {
  const [pick, setPick] = useState<Tag | null>(null);
  const sideOf = (k: string): Side | null => (chosen.start.includes(k) ? "start" : chosen.out.includes(k) ? "out" : null);
  const problems = tags.filter((t) => t.problem && sideOf(t.key));
  const cur = pick ? sideOf(pick.key) : null;
  return (
    <section className="ad-mt">
      <div className="pn-cap">
        {title}
        {note ? ` · ${note}` : ""}
      </div>
      {unread && <p className="pn-note">Meta did not answer just now. Open Audience again to try again.</p>}
      {!tags.length && !unread && empty && <p className="pn-note">{empty}</p>}
      {tags.length > 0 && (
        <div className="pn-grp">
          {tags.map((t) => {
            const side = sideOf(t.key);
            return (
              <PnRow
                key={t.key}
                onClick={editable ? () => setPick(t) : undefined}
                title={t.label}
                sub={t.count !== undefined && t.count !== null ? n(t.count) : undefined}
                value={side === "start" ? <span className="ad-acc">Start with</span> : side === "out" ? <span className="ad-vw">Leave out</span> : "Off"}
                chevron={editable}
              />
            );
          })}
        </div>
      )}
      {problems.map((t) => (
        <p className="pn-note ad-bad" key={t.key}>
          {t.problem}
        </p>
      ))}
      <ActionSheet
        open={pick !== null}
        onClose={() => setPick(null)}
        title={pick?.label}
        actions={
          pick
            ? [
                { label: cur === "start" ? "Stop starting with them" : "Start with them", onSelect: () => onMove(pick.key, cur, cur === "start" ? null : "start") },
                { label: cur === "out" ? "Stop leaving them out" : "Leave them out", onSelect: () => onMove(pick.key, cur, cur === "out" ? null : "out") },
              ]
            : []
        }
      />
    </section>
  );
}

/**
 * The goal's client lists on a phone and an iPad: each list the goal uses as a group of switches, the
 * product's other lists to add, a new list, and the pixel's people and the Meta account's audiences as rows.
 */
export function ListsNative({
  aud,
  view,
  events,
  custom,
  unread,
  editable,
  save,
  onAdd,
}: {
  aud: Audience;
  view: ListsView | null;
  events: PixelEvent[] | null;
  custom: CustomAudience[] | null;
  unread: ("events" | "custom")[];
  editable: boolean;
  save: (next: Audience, summary: string) => void;
  onAdd: () => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);
  if (!view) return <p className="pn-empty">Loading the client lists…</p>;
  const ticked = aud.lists.map((t) => ({ t, l: listFor(view.lists, t.id) })).filter((x) => x.l);
  const rest = view.lists.filter((l) => !aud.lists.some((t) => listFor(view.lists, t.id)?.id === l.id) && l.state !== "failed" && l.state !== "draft");
  const setTick = (id: string, patch: Partial<Audience["lists"][number]>, summary: string) => save({ ...aud, lists: aud.lists.map((t) => (t.id === id ? { ...t, ...patch } : t)) }, summary);
  const gone = ticked.find((x) => x.t.id === removing);
  const pxTags = pixelTags(aud, view, events);
  const cuTags = customTags(aud, custom);
  // Why: moving a tag from one side to the other is one change, so it saves as one version.
  const move = (toggle: (a: Audience, side: Side, key: string, on: boolean) => [Audience, string]) => (key: string, from: Side | null, to: Side | null) => {
    let next = aud;
    let summary = "";
    if (from) [next, summary] = toggle(next, from, key, true);
    if (to) [next, summary] = toggle(next, to, key, false);
    save(next, summary);
  };
  const showCustom = !(custom === null && !aud.custom.start.length && !aud.custom.out.length && !unread.includes("custom"));
  return (
    <>
      {!view.lists.length && <p className="pn-note">No client lists yet.</p>}
      {ticked.map(({ t, l }) => (
        <div className="pn-grp ad-q" key={t.id}>
          <div className="pn-row static wrap">
            <span className="pn-ico">
              <Icon name="users" />
            </span>
            <span className="m">
              <span className="t">
                {l!.name}: {n(l!.rowsSent)}
              </span>
              <span className="s">{l!.state === "ready" ? [found(l!), likeLine(l!)].filter(Boolean).join(" · ") : "Meta is matching them. This can take a few hours."}</span>
            </span>
          </div>
          <SwitchRow title="Start with them" on={t.start} disabled={!editable} onToggle={() => setTick(t.id, { start: !t.start, ...(t.start ? {} : { out: false }) }, `Client lists: ${t.start ? "not " : ""}starting with ${l!.name}`)} />
          <SwitchRow title="Find people like them" on={t.like} disabled={!editable || (!t.like && !l!.likeReady)} onToggle={() => setTick(t.id, { like: !t.like }, `Client lists: ${t.like ? "not " : ""}people like ${l!.name}`)} />
          <SwitchRow title="Leave them out" on={t.out} disabled={!editable} onToggle={() => setTick(t.id, { out: !t.out, ...(t.out ? {} : { start: false }) }, `Client lists: ${t.out ? "keep" : "leave out"} ${l!.name}`)} />
          {editable && <PnRow title="Take this list off" danger onClick={() => setRemoving(t.id)} />}
        </div>
      ))}
      {editable && (
        <div className="pn-grp ad-q">
          {rest.map((l) => (
            <PnRow
              key={l.id}
              className="accent"
              icon={<Icon name="plus" />}
              title={l.name}
              chevron={false}
              onClick={() => save({ ...aud, lists: [...aud.lists, { id: l.id, start: false, like: l.likeReady && !l.belowFloor, likeRatio: 0.01, out: true }] }, `Client lists: ${l.name} added`)}
            />
          ))}
          <PnRow className="accent" icon={<Icon name="upload" />} title="Add a new list" chevron={false} onClick={onAdd} />
        </div>
      )}
      {view.pixel && (
        <TagGroup
          title="From your pixel"
          note="last 180 days"
          empty={events !== null ? "Your pixel has sent no events yet." : null}
          unread={unread.includes("events")}
          tags={pxTags}
          chosen={aud.events}
          editable={editable}
          onMove={move(togglePixel)}
        />
      )}
      {showCustom && (
        <TagGroup
          title="Audiences already in your Meta account"
          empty={custom !== null ? "No audiences in your Meta ad account yet." : null}
          unread={unread.includes("custom")}
          tags={cuTags}
          chosen={{ start: aud.custom.start.map((c) => c.id), out: aud.custom.out.map((c) => c.id) }}
          editable={editable}
          onMove={move((a, side, id, on) => toggleCustom(a, cuTags, side, id, on))}
        />
      )}
      {aud.why.lists && (
        <p className="pn-note">
          <b>Why these:</b> {aud.why.lists}
        </p>
      )}
      <p className="pn-note ad-priv">
        <Icon name="shield" />
        <span>Sent to Meta only to find matches: scrambled first, never kept by Flashadz. Meta deletes what it cannot match.</span>
      </p>
      <ActionSheet
        open={!!gone}
        onClose={() => setRemoving(null)}
        title={gone ? `Take ${gone.l!.name} off this goal?` : ""}
        message="The list stays with the product."
        actions={gone ? [{ label: "Take it off", danger: true, onSelect: () => save({ ...aud, lists: aud.lists.filter((x) => x.id !== gone.t.id) }, `Client lists: ${gone.l!.name} taken off`) }] : []}
      />
    </>
  );
}
